import type { ComponentType } from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';

import BpkHorizontalNav, {
  BpkHorizontalNavItem,
} from '@skyscanner/backpack-web/bpk-component-horizontal-nav';
import BpkPanel from '@skyscanner/backpack-web/bpk-component-panel';

import type {
  OnSelectProps,
  ReactJsonViewProps,
} from '@microlink/react-json-view';

import STYLES from './DebugToolViewer.module.scss';

import { isBrowser } from '@shared/utils-universal/is-browser';

type Props = {
  jsonString: string;
};

const safelyParseJson = (jsonString: string) => {
  try {
    return JSON.parse(jsonString);
  } catch {
    return null;
  }
};

const capitalizeFirstLetter = (string: string) =>
  string.charAt(0).toUpperCase() + string.slice(1);

const isValidUrl = (text: string): boolean => {
  try {
    return Boolean(new URL(text));
  } catch {
    return false;
  }
};

const DebugToolViewer = ({ jsonString }: Props) => {
  const parsedDebugInfo = safelyParseJson(jsonString);
  const keys = parsedDebugInfo ? Object.keys(parsedDebugInfo) : [];
  const [selectedTab, setSelectedTab] = useState(keys[0] ?? '');

  const [ReactJson, setReactJson] =
    useState<ComponentType<ReactJsonViewProps> | null>(null);

  const loadJsonViewInBrowser = async () => {
    try {
      const JsonView = await import('@microlink/react-json-view');
      setReactJson(() => JsonView.default as ComponentType<ReactJsonViewProps>);
    } catch {
      // ignore error
    }
  };
  useEffect(() => {
    if (isBrowser()) {
      loadJsonViewInBrowser();
    }
  }, []);

  const jsonSrc = useMemo(() => {
    const src = parsedDebugInfo && parsedDebugInfo[selectedTab];
    if (typeof src === 'string') {
      const parsed = safelyParseJson(src);
      return parsed ?? { value: src };
    }
    return src;
  }, [parsedDebugInfo, selectedTab]);

  const handleSelect = useCallback((select: OnSelectProps) => {
    if (typeof select.value === 'string' && isValidUrl(select.value)) {
      window.open(select.value, '_blank', 'noopener,noreferrer');
    }
  }, []);

  if (!jsonString || !parsedDebugInfo || keys.length === 0) return null;

  return (
    <BpkPanel padded={false} className={STYLES.DebugToolViewer__panel}>
      <BpkHorizontalNav>
        {keys.map((key) => (
          <BpkHorizontalNavItem
            key={key}
            name={key}
            selected={selectedTab === key}
            onClick={() => setSelectedTab(key)}
          >
            {capitalizeFirstLetter(key)}
          </BpkHorizontalNavItem>
        ))}
      </BpkHorizontalNav>
      <div className={STYLES.DebugToolViewer__tab}>
        {ReactJson && jsonSrc && (
          <ReactJson
            src={jsonSrc}
            collapsed
            displayDataTypes={false}
            onSelect={handleSelect}
          />
        )}
      </div>
    </BpkPanel>
  );
};

export default DebugToolViewer;
